import { GAME_FOR_SKILL, MOTOR_TINT } from '../ui/gameArt'
import { isContentSkill, type Skill } from '../types/prisma-enums'

/**
 * The two skill meters `design-system.md` §"Progress bars" has always specified,
 * as components.
 *
 * 🔴 THE SPEC WAS WRITTEN AND NOT DRAWN. §"Progress bars — 10–12 px,
 * skill-coloured" is explicit: *"Content skills render as **five segments**
 * (`Level 3 of 5`); motor skills render as a **single filled bar** (`81%`)"*.
 * The teacher's student page rendered both as plain right-aligned text — a
 * column of "Level 3 of 3" and "12 activities" — so the one screen a teacher
 * opens to answer *"where is this child"* made her read eight numbers instead of
 * seeing a shape. The rule below it, *"a percentage where a level is the honest
 * answer is a bug"*, is why these are two components and not one with a
 * `percent` prop.
 *
 * 🔴 SEGMENTS FOR LEVELS, BECAUSE A LEVEL IS ORDINAL AND NOT A FRACTION.
 * `prisma-enums.ts` says it for the data: *"never show a percentage where a
 * level is the honest answer"*. A continuous bar at 60 % invites "three fifths
 * of the way through numeracy", which is not what level 3 of 5 means — the
 * levels are not equal in size and the last one does not end the skill. Discrete
 * blocks can only be counted, which is the only reading that is true.
 *
 * ⚠️ THE COLOUR COMES FROM THE TWO MAPS THAT ALREADY HOLD IT, never a third
 * table — `GAME_FOR_SKILL` for content skills, `MOTOR_TINT` for motor ones. This
 * is `Charts.tsx`'s `skillTint` reasoning, and the function is duplicated here
 * rather than imported because that one lives in the PARENT feature folder: a
 * teacher screen reaching into another tier's directory is the import that makes
 * two tiers impossible to change independently. The maps are shared; the
 * six-line reader over them is not worth a cross-feature dependency.
 *
 * ⚠️ EVERY METER IS `aria-hidden`, AND THAT IS NOT AN OVERSIGHT. Each one is
 * drawn beside its own text label and figure — "Numbers · Level 3 of 3" — which
 * a screen reader already reads. Announcing the bar as well would say the same
 * fact twice, and §12's rule is that colour and shape are never the only
 * carrier: here they are the *second* carrier, which is the right way round.
 */
export function skillTint(skill: Skill): string {
  return isContentSkill(skill) ? GAME_FOR_SKILL[skill].tint : MOTOR_TINT[skill]
}

export interface LevelMeterProps {
  skill: Skill
  /** Levels finished. `0` is a real state — the child has not started. */
  completed: number
  /** How many levels the skill has. */
  of: number
}

/**
 * A content skill, as blocks.
 *
 * ⚠️ AN UNSTARTED SKILL IS FIVE EMPTY BLOCKS, NOT A HIDDEN BAR. "Nothing yet" is
 * the finding a teacher is looking for on this screen, and a meter that vanishes
 * when it reaches zero hides exactly the row she needed to see.
 */
export function LevelMeter({ skill, completed, of }: LevelMeterProps) {
  const tint = skillTint(skill)
  const done = Math.max(0, Math.min(completed, of))

  return (
    <span aria-hidden="true" className="flex items-center gap-1">
      {Array.from({ length: of }, (_, i) => (
        <span
          key={i}
          className="h-2.5 flex-1 rounded-pill"
          style={{
            /*
             * ⚠️ THE EMPTY BLOCKS ARE A TINT OF THE SKILL'S OWN COLOUR, not a
             * grey — §1: *"no grey, anywhere"*. At 18 % they read as "this block
             * belongs to this bar and is not filled" rather than as a separate
             * neutral element, which is what makes the row scannable as one
             * meter instead of two colours.
             */
            background:
              i < done ? tint : `color-mix(in srgb, ${tint} 18%, transparent)`,
          }}
        />
      ))}
    </span>
  )
}

export interface ActivityMeterProps {
  skill: Skill
  /** This skill's activity count. */
  events: number
  /**
   * The largest count among the siblings drawn beside it.
   *
   * 🔴 THE BAR IS RELATIVE TO THE ROW ABOVE IT, NOT TO A TARGET, and the caller
   * supplies the scale because only the caller can see the set. A motor skill
   * has no denominator in the data — there is no "of 5" — so the honest reading
   * is *"more of this than that"*. Scaling each bar against its own value would
   * draw three full bars and say nothing at all.
   */
  max: number
}

/**
 * A motor skill, as one filled bar.
 *
 * ⚠️ IT IS NOT LABELLED WITH A PERCENTAGE, and the caller must not add one.
 * §"Progress bars" allows `81%` for a motor skill because that tier HAS a
 * denominator; this screen's number is a raw activity count, and printing a
 * share of the busiest sibling as a percent would invent a measurement.
 */
export function ActivityMeter({ skill, events, max }: ActivityMeterProps) {
  const tint = skillTint(skill)
  /* A set with no activity at all is three empty tracks, not a division by zero. */
  const share = max > 0 ? Math.min(1, events / max) : 0

  return (
    <span
      aria-hidden="true"
      className="block h-2.5 overflow-hidden rounded-pill"
      style={{ background: `color-mix(in srgb, ${tint} 18%, transparent)` }}
    >
      <span
        className="play-fill block h-full rounded-pill"
        style={{ width: `${String(Math.round(share * 100))}%`, background: tint }}
      />
    </span>
  )
}
